import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Lock,
  Mic,
  Video,
  FileText,
  Activity,
  Eye,
  MessageSquare,
  TrendingUp,
  TrendingDown,
  Home,
  Landmark,
  ArrowRight,
  CheckCircle2,
  Trash2,
  Download,
  Link2,
  Copy,
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  BarChart3,
  ChevronDown,
  ChevronRight,
  QrCode,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { QRCodeSVG } from "qrcode.react";
import { recordRetentionEvent, type Retention as TelemetryRetention } from "@/lib/selfDebateTelemetry";
import {
  runUploadPreflight,
  runBatchUploadPreflight,
  DEFAULT_PREFLIGHT_LIMITS,
  type CaptureKind,
  type PreflightResult,
  type BatchPreflightResult,
  type PreflightInput,
  SUPPORTED_CODECS,
  friendlyLimit,
} from "@/lib/selfDebatePreflight";
import { downloadAnalyzerReportPdf } from "@/lib/selfDebateReportPdf";
import {
  saveLastBatchAttempt,
  loadLastBatchAttempt,
} from "@/lib/lastBatchAttempt";
import { copyTextWithFallback } from "@/lib/clipboardFallback";
import { toast } from "@/hooks/use-toast";

/* ─────────────────────────────────────────────────────────────
 * Self-Debate Studio Preview
 *
 * Lives on the landing page. Bundles four landing-only previews so
 * a visitor can understand — without signing in — what the actual
 * Oratory Studio workflow will give them:
 *
 *   1. One-step "Where will you debate?" onboarding (Home vs Office)
 *   2. Rehearsal templates (Opening / Rebuttal / Closing) with
 *      guided text/audio/video upload prompts
 *   3. Sample Post-Presentation AI Coach Analyzer report with
 *      gesture, vocal tone, delivery, and content corrections
 *   4. Sovereign-tier privacy + retention toggle
 *
 * All UI is presentational — every CTA deep-links into
 * /oratory-studio with a `?source=` and selected mode/template
 * so the studio can hydrate the right context.
 * ───────────────────────────────────────────────────────────── */

type RehearsalMode = "home" | "office";
type Template = "opening" | "rebuttal" | "closing";
type Retention = "session" | "30d" | "90d";

const MODES: Array<{
  id: RehearsalMode;
  label: string;
  desc: string;
  icon: typeof Home;
  modules: string[];
}> = [
  {
    id: "home",
    label: "Rehearsing at home",
    desc: "Private practice. Looser scoring, focus on fluency, pacing, and confidence.",
    icon: Home,
    modules: ["Pacing drill", "Filler-word counter", "Camera presence"],
  },
  {
    id: "office",
    label: "Practicing for a specific office",
    desc: "Campaign-grade scoring. Adds fact integrity, opposition framing, and electoral impact.",
    icon: Landmark,
    modules: ["Fact-Armor (AFF)", "Press Gauntlet", "Electoral Impact Score"],
  },
];

const TEMPLATES: Array<{
  id: Template;
  title: string;
  duration: string;
  prompt: string;
  guidance: string[];
}> = [
  {
    id: "opening",
    title: "Opening Statement",
    duration: "90 sec",
    prompt: "State your name, the office you seek, and the single change you will deliver.",
    guidance: [
      "Hook in the first 7 seconds",
      "One concrete promise, no jargon",
      "Land on a memorable closing line",
    ],
  },
  {
    id: "rebuttal",
    title: "Rebuttal",
    duration: "60 sec",
    prompt: "Respond to the strongest attack against your record without losing composure.",
    guidance: [
      "Acknowledge → reframe → redirect",
      "Cite one verifiable data point",
      "End on offense, not defense",
    ],
  },
  {
    id: "closing",
    title: "Closing Argument",
    duration: "120 sec",
    prompt: "Tell the voter exactly what tomorrow looks like if they choose you.",
    guidance: [
      "Repeat your core promise",
      "Speak directly to the camera",
      "End with a call to action",
    ],
  },
];

const RETENTION_OPTIONS: Array<{
  id: Retention;
  label: string;
  desc: string;
}> = [
  {
    id: "session",
    label: "Session-only (Ghost Mode)",
    desc: "Recording is analyzed in-memory and deleted the moment you close the tab.",
  },
  {
    id: "30d",
    label: "30 days",
    desc: "Stored encrypted so you can compare progress week-over-week.",
  },
  {
    id: "90d",
    label: "90 days",
    desc: "Full campaign-cycle review. Auto-purged on day 91.",
  },
];

/* Sample analyzer scores — purely illustrative, shown to anonymous
 * visitors so they can see the depth of the real after-action report. */
const SAMPLE_SCORES = [
  { label: "Gesture", score: 74, icon: Activity, note: "Hands above podium 62% of session — open and trustworthy." },
  { label: "Vocal Tone", score: 81, icon: Mic, note: "Strong projection. 4 filler words detected at 0:42, 1:18." },
  { label: "Delivery", score: 78, icon: Eye, note: "Eye contact 71%. Brief stress micro-expression at 2:05." },
  { label: "Content", score: 69, icon: MessageSquare, note: "2 unsupported claims. Fact-Armor flagged a citation gap." },
];

const SAMPLE_CORRECTIONS = [
  { type: "gesture", text: "Lower right hand during the second pause — currently reads as defensive.", trend: "down" as const },
  { type: "vocal", text: "Replace 'um' at 0:42 with a 1-beat pause to project authority.", trend: "up" as const },
  { type: "delivery", text: "Hold gaze on camera 2 beats longer at the closing line.", trend: "up" as const },
  { type: "content", text: "Anchor the jobs claim with the Q2 BLS figure before delivery.", trend: "down" as const },
];

const SelfDebateStudioPreview = () => {
  const [mode, setMode] = useState<RehearsalMode>("home");
  const [template, setTemplate] = useState<Template>("opening");
  const [retention, setRetention] = useState<Retention>("session");
  const [consentRetention, setConsentRetention] = useState(false);
  const [consentDataNotice, setConsentDataNotice] = useState(false);
  const [preflight, setPreflight] = useState<PreflightResult | null>(null);
  const [preflightKind, setPreflightKind] = useState<CaptureKind | null>(null);
  const [batchPreflight, setBatchPreflight] = useState<BatchPreflightResult | null>(null);
  const lastBatchInputsRef = useRef<PreflightInput[] | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const lastTelemetryRef = useRef<Retention | null>(null);
  const [samplePdfBusy, setSamplePdfBusy] = useState(false);
  const [samplePdfProgress, setSamplePdfProgress] = useState(0);
  const [samplePdfStage, setSamplePdfStage] = useState<string>("");
  const [samplePdfLabel, setSamplePdfLabel] = useState<string>("");
  const [matrixCopied, setMatrixCopied] = useState<string | null>(null);
  const [matrixModeFilter, setMatrixModeFilter] = useState<"current" | "all">("current");
  const [batchAttempt, setBatchAttempt] = useState(0);
  const [batchLastAction, setBatchLastAction] = useState<"initial" | "retry">("initial");
  const [matrixFilterCopied, setMatrixFilterCopied] = useState(false);
  const [hydratedBatchFromCache, setHydratedBatchFromCache] = useState(false);
  const [failedDetailsOpen, setFailedDetailsOpen] = useState(false);
  const [showMatrixQr, setShowMatrixQr] = useState(false);
  const [manualCopyText, setManualCopyText] = useState<string | null>(null);

  const selectedTemplate = TEMPLATES.find((t) => t.id === template)!;
  const selectedMode = MODES.find((m) => m.id === mode)!;
  const overall = Math.round(
    SAMPLE_SCORES.reduce((sum, s) => sum + s.score, 0) / SAMPLE_SCORES.length
  );

  const studioHref = `/oratory-studio?source=self-debate-preview&mode=${mode}&template=${template}&retention=${retention}`;

  /* Shareable absolute URL — re-opens the preview on another device with
   * the same mode / template / retention pre-selected via query string. */
  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const u = new URL(window.location.origin + "/");
    u.hash = `self-debate?mode=${mode}&template=${template}&retention=${retention}`;
    return u.toString();
  }, [mode, template, retention]);

  /* On mount: hydrate from URL hash so a deep-link card "lands" on the
   * same selections the sender saw. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const raw = window.location.hash.replace(/^#/, "");
    if (!raw.startsWith("self-debate")) return;
    const params = new URLSearchParams(raw.split("?")[1] ?? "");
    const m = params.get("mode");
    const t = params.get("template");
    const r = params.get("retention");
    const mf = params.get("matrix");
    if (m === "home" || m === "office") setMode(m);
    if (t === "opening" || t === "rebuttal" || t === "closing") setTemplate(t);
    if (r === "session" || r === "30d" || r === "90d") setRetention(r);
    if (mf === "current" || mf === "all") setMatrixModeFilter(mf);
  }, []);

  /* Hydrate the last batch attempt (mode/template/lastAction/files) so the
   * retry context survives a refresh of the landing-page preview. */
  useEffect(() => {
    const last = loadLastBatchAttempt();
    if (!last) return;
    setMode(last.mode);
    setTemplate(last.template);
    setBatchLastAction(last.lastAction);
    setBatchAttempt(last.attempt);
    setHydratedBatchFromCache(true);
    if (typeof last.failedDetailsOpen === "boolean") {
      setFailedDetailsOpen(last.failedDetailsOpen);
    }
    // Re-hydrate the cached batch inputs so "Try again" can re-run validation.
    if (last.files.length > 0) {
      const inputs: PreflightInput[] = last.files.map((f) => ({
        kind: f.kind,
        sizeBytes: f.sizeBytes,
        label: f.label,
      }));
      lastBatchInputsRef.current = inputs;
      // Re-compute the result so the UI shows the same first-failure as
      // before the refresh — without forcing the user to click Run again.
      setBatchPreflight(runBatchUploadPreflight(inputs));
    }
  }, []);

  /* Fire-and-forget telemetry whenever the user changes retention.
   * Skips the first paint so we only measure deliberate selections. */
  useEffect(() => {
    if (lastTelemetryRef.current === retention) return;
    lastTelemetryRef.current = retention;
    recordRetentionEvent({
      retention: retention as TelemetryRetention,
      mode,
      template,
      source: "self-debate-preview",
    });
  }, [retention, mode, template]);

  const consentReady = consentRetention && consentDataNotice;
  const launchHref = consentReady
    ? `${studioHref}&consent=1`
    : studioHref;

  /* Preflight runner — exposed for each capture kind. Uses representative
   * sizes so visitors can see both pass and fail flows without a real upload. */
  const runPreflightFor = (kind: CaptureKind) => {
    const sample =
      kind === "text"
        ? selectedTemplate.prompt.length // tiny → passes
        : kind === "audio"
        ? 8 * 1024 * 1024 // ~8 MB → passes
        : 320 * 1024 * 1024; // 320 MB → fails (over 200 MB)
    const result = runUploadPreflight({ kind, sizeBytes: sample });
    setPreflightKind(kind);
    setPreflight(result);
    if (result.ok) {
      toast({
        title: `Preflight passed for ${kind}`,
        description: `Within ${(result.maxBytes / (1024 * 1024)).toFixed(0)} MB limit. Analyzer can start.`,
      });
    }
  };

  /** Validate the full multi-file batch (text + audio + video). The same
   *  inputs are re-used by the Try-again button so users can re-validate
   *  after replacing a file. */
  const runBatch = (inputs: PreflightInput[]) => {
    lastBatchInputsRef.current = inputs;
    const result = runBatchUploadPreflight(inputs);
    setBatchPreflight(result);
    if (result.ok) {
      toast({ title: "Batch preflight passed", description: "All files within limits — analyzer can start." });
    }
  };

  const runSampleBatch = () => {
    setBatchLastAction("initial");
    setBatchAttempt(1);
    setHydratedBatchFromCache(false);
    setFailedDetailsOpen(false);
    const inputs: PreflightInput[] = [
      { kind: "text", sizeBytes: selectedTemplate.prompt.length, label: "opening-notes.txt" },
      { kind: "audio", sizeBytes: 8 * 1024 * 1024, label: "rebuttal-take-2.m4a" },
      // Intentionally oversize so visitors can see the first-failure flow.
      { kind: "video", sizeBytes: 320 * 1024 * 1024, label: "closing-1080p.mp4" },
    ];
    runBatch(inputs);
    saveLastBatchAttempt({
      mode,
      template,
      lastAction: "initial",
      attempt: 1,
      files: inputs.map((f) => ({ kind: f.kind, label: f.label ?? f.kind, sizeBytes: f.sizeBytes })),
      failedDetailsOpen: false,
    });
  };

  const retryBatch = () => {
    if (!lastBatchInputsRef.current) return;
    const nextAttempt = batchAttempt + 1;
    setBatchLastAction("retry");
    setBatchAttempt(nextAttempt);
    setHydratedBatchFromCache(false);
    runBatch(lastBatchInputsRef.current);
    saveLastBatchAttempt({
      mode,
      template,
      lastAction: "retry",
      attempt: nextAttempt,
      files: lastBatchInputsRef.current.map((f) => ({
        kind: f.kind,
        label: f.label ?? f.kind,
        sizeBytes: f.sizeBytes,
      })),
      failedDetailsOpen,
    });
  };

  const handleDownloadSamplePdf = () => {
    setSamplePdfBusy(true);
    setSamplePdfProgress(0);
    setSamplePdfStage("init");
    setSamplePdfLabel("Preparing document");
    try {
    downloadAnalyzerReportPdf(
      {
        title: "Sample Post-Presentation Analyzer Report",
        subtitle: `Sample · ${selectedTemplate.title} · ${selectedMode.label}`,
        composite: overall,
        scores: SAMPLE_SCORES.map((s) => ({ label: s.label, score: s.score, note: s.note })),
        corrections: SAMPLE_CORRECTIONS.map((c) => ({ type: c.type, text: c.text, trend: c.trend })),
        isSample: true,
        generatedAt: new Date(),
      },
      `the-floor-sample-analyzer-${template}.pdf`,
      (p) => {
        setSamplePdfProgress(p.percent);
        setSamplePdfStage(p.stage);
        setSamplePdfLabel(p.label);
      },
    );
    } catch (err) {
      setSamplePdfProgress(100);
      setSamplePdfStage("error");
      setSamplePdfLabel("Failed");
      toast({
        title: "PDF generation failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      window.setTimeout(() => {
        setSamplePdfBusy(false);
        setSamplePdfProgress(0);
        setSamplePdfStage("");
        setSamplePdfLabel("");
      }, 350);
    }
  };

  const handleCopyShare = async () => {
    const result = await copyTextWithFallback(shareUrl);
    if (!result.ok) {
      setManualCopyText(result.text ?? shareUrl);
      return;
    }
    setShareCopied(true);
    window.setTimeout(() => setShareCopied(false), 1800);
    toast({
      title: result.fallback ? "Deep-link copied (fallback)" : "Deep-link copied",
      description: "Open it on another device to land on these settings.",
    });
  };

  /**
   * Build a deep-link for an arbitrary mode/template/retention combination
   * (independent of the user's current selection). Used by the share matrix.
   */
  const buildShareUrl = (
    m: RehearsalMode,
    t: Template,
    r: Retention,
    matrix?: "current" | "all",
  ): string => {
    if (typeof window === "undefined") return "";
    const u = new URL(window.location.origin + "/");
    const matrixParam = matrix ? `&matrix=${matrix}` : "";
    u.hash = `self-debate?mode=${m}&template=${t}&retention=${r}${matrixParam}`;
    return u.toString();
  };

  const handleCopyMatrix = async (m: RehearsalMode, t: Template, r: Retention) => {
    const key = `${m}-${t}-${r}`;
    const url = buildShareUrl(m, t, r);
    const result = await copyTextWithFallback(url);
    if (!result.ok) {
      setManualCopyText(result.text ?? url);
      return;
    }
    setMatrixCopied(key);
    window.setTimeout(() => setMatrixCopied((curr) => (curr === key ? null : curr)), 1800);
    toast({ title: "Deep-link copied", description: `${t} · ${r} retention · ${m} mode` });
  };

  /** Copy a deep-link that opens the matrix card pre-filtered to the same
   *  Current/All view the user is looking at right now. */
  const handleCopyMatrixFilter = async () => {
    const url = buildShareUrl(mode, template, retention, matrixModeFilter);
    const result = await copyTextWithFallback(url);
    if (!result.ok) {
      setManualCopyText(result.text ?? url);
      return;
    }
    setMatrixFilterCopied(true);
    window.setTimeout(() => setMatrixFilterCopied(false), 1800);
    toast({
      title: "Matrix filter link copied",
      description: `Recipients will land in the "${
        matrixModeFilter === "current" ? "Current mode" : "All modes"
      }" view.`,
    });
  };

  return (
    <section
      data-testid="self-debate-studio-preview"
      className="relative border-y border-sovereign-gold/15 bg-gradient-to-b from-secondary/30 via-background to-background px-6 py-20"
    >
      <div className="mx-auto max-w-[1200px]">
        {/* Header */}
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <span className="mb-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-sovereign-gold">
            <Shield className="h-3 w-3" /> Self-Debate Studio · Preview
          </span>
          <h2 className="font-serif text-3xl font-bold leading-tight tracking-tight text-foreground md:text-4xl">
            Try the <span className="text-gradient-gold">Coach Workflow</span> Before You Sign In.
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Pick where you'll debate, choose a rehearsal template, see a real sample of the
            after-action report, and decide how long your recording is kept.
          </p>
        </div>

        {/* 1 — One-step onboarding */}
        <div
          data-testid="onboarding-mode-selector"
          className="mb-10 rounded-2xl border border-sovereign-gold/15 bg-card/40 p-6 backdrop-blur md:p-8"
        >
          <p className="mb-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Step 1 · Where are you debating?
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {MODES.map((m) => {
              const Icon = m.icon;
              const active = m.id === mode;
              return (
                <button
                  type="button"
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  data-testid={`mode-${m.id}`}
                  aria-pressed={active}
                  className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-all ${
                    active
                      ? "border-sovereign-gold bg-sovereign-gold/10 shadow-[0_0_0_1px_hsl(var(--sovereign-gold)/0.3)]"
                      : "border-border/40 bg-background/40 hover:border-sovereign-gold/40"
                  }`}
                >
                  <Icon
                    className={`mt-0.5 h-5 w-5 shrink-0 ${
                      active ? "text-sovereign-gold" : "text-muted-foreground"
                    }`}
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">{m.label}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{m.desc}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.modules.map((mod) => (
                        <span
                          key={mod}
                          className="rounded-full border border-border/40 bg-background/60 px-2 py-0.5 font-mono text-[10px] text-muted-foreground"
                        >
                          {mod}
                        </span>
                      ))}
                    </div>
                  </div>
                  {active && (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-sovereign-gold" />
                  )}
                </button>
              );
            })}
          </div>
          <p
            data-testid="mode-summary"
            className="mt-4 text-xs text-muted-foreground"
          >
            Coach modules tuned for{" "}
            <strong className="text-foreground">{selectedMode.label.toLowerCase()}</strong>:{" "}
            {selectedMode.modules.join(" · ")}
          </p>
        </div>

        {/* 2 — Rehearsal templates */}
        <div
          data-testid="rehearsal-templates"
          className="mb-10 rounded-2xl border border-sovereign-gold/15 bg-card/40 p-6 backdrop-blur md:p-8"
        >
          <p className="mb-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Step 2 · Pick a rehearsal template
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {TEMPLATES.map((t) => {
              const active = t.id === template;
              return (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => setTemplate(t.id)}
                  data-testid={`template-${t.id}`}
                  aria-pressed={active}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    active
                      ? "border-sovereign-gold bg-sovereign-gold/10"
                      : "border-border/40 bg-background/40 hover:border-sovereign-gold/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-foreground">{t.title}</p>
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {t.duration}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">{t.prompt}</p>
                </button>
              );
            })}
          </div>

          {/* Guided upload prompts */}
          <div className="mt-6 rounded-xl border border-border/40 bg-background/30 p-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Guided prompts for {selectedTemplate.title}
            </p>
            <ul className="mt-3 space-y-1.5">
              {selectedTemplate.guidance.map((g) => (
                <li
                  key={g}
                  className="flex items-start gap-2 text-sm text-foreground/85"
                >
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sovereign-gold" />
                  {g}
                </li>
              ))}
            </ul>

            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <Link
                to={`${studioHref}&capture=text`}
                data-testid="upload-text"
                className="flex items-center justify-center gap-2 rounded-lg border border-border/50 bg-background/60 p-3 text-sm font-medium text-foreground transition-colors hover:border-sovereign-gold/60"
              >
                <FileText className="h-4 w-4 text-sovereign-gold" /> Upload text
              </Link>
              <Link
                to={`${studioHref}&capture=audio`}
                data-testid="upload-audio"
                className="flex items-center justify-center gap-2 rounded-lg border border-border/50 bg-background/60 p-3 text-sm font-medium text-foreground transition-colors hover:border-sovereign-gold/60"
              >
                <Mic className="h-4 w-4 text-sovereign-gold" /> Record audio
              </Link>
              <Link
                to={`${studioHref}&capture=video`}
                data-testid="upload-video"
                className="flex items-center justify-center gap-2 rounded-lg border border-border/50 bg-background/60 p-3 text-sm font-medium text-foreground transition-colors hover:border-sovereign-gold/60"
              >
                <Video className="h-4 w-4 text-sovereign-gold" /> Record video
              </Link>
            </div>
          </div>
        </div>

        {/* 3 — Sample Analyzer report */}
        <div
          data-testid="sample-analyzer-report"
          className="mb-10 rounded-2xl border border-sovereign-gold/15 bg-card/40 p-6 backdrop-blur md:p-8"
        >
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Step 3 · Sample after-action report
              </p>
              <h3 className="mt-1 font-serif text-2xl font-bold text-foreground">
                Post-Presentation AI Coach · Preview
              </h3>
            </div>
            <div className="text-right">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Composite
              </p>
              <p
                data-testid="sample-composite-score"
                className="font-mono text-3xl font-bold text-gradient-gold"
              >
                {overall}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {SAMPLE_SCORES.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  data-testid={`sample-score-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-xl border border-border/40 bg-background/40 p-3"
                >
                  <div className="mb-1.5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-sovereign-gold" />
                      <span className="text-sm font-medium text-foreground">
                        {s.label}
                      </span>
                    </div>
                    <span
                      className={`font-mono text-sm font-bold ${
                        s.score >= 80
                          ? "text-tactical-green"
                          : s.score >= 70
                          ? "text-tactical-amber"
                          : "text-tactical-red"
                      }`}
                    >
                      {s.score}
                    </span>
                  </div>
                  <Progress value={s.score} className="mb-1.5 h-1.5" />
                  <p className="text-xs text-muted-foreground">{s.note}</p>
                </div>
              );
            })}
          </div>

          {/* Highlighted corrections */}
          <div className="mt-5 rounded-xl border border-sovereign-gold/20 bg-sovereign-gold/5 p-4">
            <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-sovereign-gold">
              Top Corrections — Apply Before Next Take
            </p>
            <ul className="space-y-2">
              {SAMPLE_CORRECTIONS.map((c, i) => (
                <li
                  key={i}
                  data-testid={`sample-correction-${c.type}`}
                  className="flex items-start gap-2 text-sm text-foreground/90"
                >
                  {c.trend === "up" ? (
                    <TrendingUp className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tactical-green" />
                  ) : (
                    <TrendingDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tactical-amber" />
                  )}
                  <span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      [{c.type}]
                    </span>{" "}
                    {c.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* 4 — Privacy + retention */}
        <div
          data-testid="privacy-retention-controls"
          className="mb-10 rounded-2xl border border-sovereign-gold/20 bg-card/40 p-6 backdrop-blur md:p-8"
        >
          <div className="mb-4 flex items-start gap-3">
            <Lock className="mt-0.5 h-5 w-5 shrink-0 text-sovereign-gold" />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-sovereign-gold">
                Sovereign-Tier Data Handling
              </p>
              <h3 className="mt-1 font-serif text-2xl font-bold text-foreground">
                Your rehearsal. Your rules.
              </h3>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                Self-debate recordings are processed under our Sovereign privacy posture:
                end-to-end encrypted in transit and at rest, never used to train external
                models, and{" "}
                <strong className="text-foreground">purged automatically</strong> based on
                the retention you choose below. Sovereign-tier sessions can be marked{" "}
                <strong className="text-foreground">Ghost Mode</strong> so raw audio and
                video never touch persistent storage.
              </p>
            </div>
          </div>

          <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Step 4 · Choose retention
          </p>
          <div
            role="radiogroup"
            aria-label="Retention period"
            className="grid grid-cols-1 gap-3 md:grid-cols-3"
          >
            {RETENTION_OPTIONS.map((opt) => {
              const active = opt.id === retention;
              return (
                <button
                  type="button"
                  key={opt.id}
                  role="radio"
                  aria-checked={active}
                  onClick={() => setRetention(opt.id)}
                  data-testid={`retention-${opt.id}`}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    active
                      ? "border-sovereign-gold bg-sovereign-gold/10"
                      : "border-border/40 bg-background/40 hover:border-sovereign-gold/40"
                  }`}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <p className="font-semibold text-foreground">{opt.label}</p>
                    {active && (
                      <CheckCircle2 className="h-4 w-4 text-sovereign-gold" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{opt.desc}</p>
                </button>
              );
            })}
          </div>

          <p
            data-testid="retention-summary"
            className="mt-4 flex items-center gap-2 text-xs text-muted-foreground"
          >
            <Trash2 className="h-3.5 w-3.5 text-sovereign-gold" />
            Selected:{" "}
            <strong className="text-foreground">
              {RETENTION_OPTIONS.find((r) => r.id === retention)!.label}
            </strong>
          </p>
        </div>

        {/* 5 — Sovereign-tier consent checklist */}
        <div
          data-testid="sovereign-consent-checklist"
          className="mb-10 rounded-2xl border border-sovereign-gold/30 bg-card/40 p-6 backdrop-blur md:p-8"
        >
          <div className="mb-4 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-sovereign-gold" />
            <p className="font-mono text-[10px] uppercase tracking-widest text-sovereign-gold">
              Step 5 · Sovereign-tier consent (required before recording)
            </p>
          </div>
          <ul className="space-y-3">
            <li>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/40 bg-background/40 p-3 hover:border-sovereign-gold/40">
                <input
                  type="checkbox"
                  data-testid="consent-retention"
                  checked={consentRetention}
                  onChange={(e) => setConsentRetention(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-sovereign-gold"
                />
                <span className="text-sm text-foreground/90">
                  I confirm my retention choice:{" "}
                  <strong className="text-foreground">
                    {RETENTION_OPTIONS.find((r) => r.id === retention)!.label}
                  </strong>
                  . Recording will be auto-purged accordingly.
                </span>
              </label>
            </li>
            <li>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/40 bg-background/40 p-3 hover:border-sovereign-gold/40">
                <input
                  type="checkbox"
                  data-testid="consent-data-notice"
                  checked={consentDataNotice}
                  onChange={(e) => setConsentDataNotice(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-sovereign-gold"
                />
                <span className="text-sm text-foreground/90">
                  I have read the Sovereign-tier data handling notice: encrypted in
                  transit/at rest, never used to train external models, never shared
                  without my explicit instruction.
                </span>
              </label>
            </li>
          </ul>
          <p
            data-testid="consent-status"
            className={`mt-3 font-mono text-[11px] ${
              consentReady ? "text-tactical-green" : "text-tactical-amber"
            }`}
          >
            {consentReady
              ? "✓ Consent confirmed — recording can begin."
              : "Both boxes must be checked before the recorder unlocks."}
          </p>
        </div>

        {/* 6 — Upload preflight */}
        <div
          data-testid="upload-preflight"
          className="mb-10 rounded-2xl border border-sovereign-gold/15 bg-card/40 p-6 backdrop-blur md:p-8"
        >
          <p className="mb-4 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Step 6 · Validate upload before analysis starts
          </p>
          <p className="mb-4 text-sm text-muted-foreground">
            We check size and format <strong className="text-foreground">before</strong>{" "}
            opening the recorder, so the AI Coach never starts on a payload it can't
            finish. Limits — text {friendlyLimit("text")}, audio {friendlyLimit("audio")},
            video {friendlyLimit("video")}.
          </p>
          <ul
            data-testid="preflight-codec-hints"
            className="mb-4 grid grid-cols-1 gap-2 text-[11px] text-muted-foreground sm:grid-cols-3"
          >
            {(["text", "audio", "video"] as const).map((k) => (
              <li
                key={k}
                data-testid={`preflight-codec-${k}`}
                className="rounded-md border border-border/40 bg-background/40 p-2"
              >
                <span className="font-mono uppercase text-foreground">{k}</span> · {friendlyLimit(k)} ·{" "}
                <span className="text-foreground/80">{SUPPORTED_CODECS[k].slice(0, 2).join(", ")}</span>
              </li>
            ))}
          </ul>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {(["text", "audio", "video"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => runPreflightFor(k)}
                data-testid={`preflight-${k}`}
                className="flex items-center justify-center gap-2 rounded-lg border border-border/50 bg-background/60 p-3 text-sm font-medium text-foreground transition-colors hover:border-sovereign-gold/60"
              >
                {k === "text" && <FileText className="h-4 w-4 text-sovereign-gold" />}
                {k === "audio" && <Mic className="h-4 w-4 text-sovereign-gold" />}
                {k === "video" && <Video className="h-4 w-4 text-sovereign-gold" />}
                Preflight {k}
              </button>
            ))}
          </div>

          {preflight && (
            <div
              data-testid="preflight-result"
              data-preflight-ok={preflight.ok ? "true" : "false"}
              data-preflight-code={preflight.code ?? ""}
              className={`mt-4 rounded-lg border p-4 text-sm ${
                preflight.ok
                  ? "border-tactical-green/40 bg-tactical-green/10 text-tactical-green"
                  : "border-tactical-red/40 bg-tactical-red/10 text-tactical-red"
              }`}
            >
              <div className="flex items-start gap-2">
                {preflight.ok ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                ) : (
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                )}
                <div className="flex-1">
                  <p className="font-semibold">
                    {preflight.ok
                      ? `${preflightKind} preflight passed — analysis can begin.`
                      : `${preflightKind} preflight failed`}
                  </p>
                  {!preflight.ok && (
                    <p className="mt-1 text-foreground/80">{preflight.error}</p>
                  )}
                  {!preflight.ok && preflight.guidance && (
                    <ul
                      data-testid="preflight-guidance"
                      className="mt-2 list-inside list-disc space-y-0.5 text-[12px] text-foreground/80"
                    >
                      {preflight.guidance.map((g) => (
                        <li key={g}>{g}</li>
                      ))}
                    </ul>
                  )}
                  {!preflight.ok && preflight.supportedCodecs && (
                    <p
                      data-testid="preflight-codecs"
                      className="mt-2 font-mono text-[10px] text-muted-foreground"
                    >
                      Supported: {preflight.supportedCodecs.join(" · ")}
                    </p>
                  )}
                </div>
                {!preflight.ok && preflight.retryable && preflightKind && (
                  <button
                    type="button"
                    data-testid="preflight-retry"
                    onClick={() => runPreflightFor(preflightKind)}
                    className="flex items-center gap-1 rounded-md border border-tactical-red/40 px-2 py-1 text-xs hover:bg-tactical-red/20"
                  >
                    <RefreshCw className="h-3 w-3" /> Retry
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Multi-file batch preflight + Try-again */}
          <div className="mt-6 rounded-xl border border-border/40 bg-background/30 p-4" data-testid="batch-preflight-card">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Multi-file batch preflight
                </p>
                {hydratedBatchFromCache && (
                  <span
                    data-testid="batch-resumed-badge"
                    data-attempt={batchAttempt}
                    data-last-action={batchLastAction}
                    className="inline-flex items-center gap-1 rounded-md border border-sovereign-gold/40 bg-sovereign-gold/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-sovereign-gold"
                  >
                    Resumed · attempt #{batchAttempt} · {batchLastAction}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={runSampleBatch}
                data-testid="batch-preflight-run"
                className="flex items-center gap-1 rounded-md border border-sovereign-gold/40 px-2 py-1 text-xs text-sovereign-gold hover:bg-sovereign-gold/10"
              >
                Run batch
              </button>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">
              Validate every file in a take before recording. We surface the{" "}
              <strong className="text-foreground">first failing file</strong> so you know
              exactly which one to swap before retrying.
            </p>

            {batchPreflight && (
              <div
                data-testid="batch-preflight-result"
                data-batch-ok={batchPreflight.ok ? "true" : "false"}
                data-failed-count={batchPreflight.failedCount}
              >
                <ul className="mb-3 space-y-1.5">
                  {batchPreflight.results.map((r, idx) => (
                    <li
                      key={`${r.kind}-${idx}`}
                      data-testid={`batch-file-${idx}`}
                      data-file-ok={r.ok ? "true" : "false"}
                      className={`rounded-md border p-2 text-xs ${
                        r.ok
                          ? "border-tactical-green/30 bg-tactical-green/5 text-foreground"
                          : "border-tactical-red/40 bg-tactical-red/10 text-foreground"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {r.ok ? (
                          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tactical-green" />
                        ) : (
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tactical-red" />
                        )}
                        <div className="flex-1">
                          <p>
                            <span className="font-mono uppercase text-muted-foreground">
                              [{r.kind}]
                            </span>{" "}
                            <strong className="text-foreground">{r.label}</strong>
                          </p>
                          {!r.ok && r.error && (
                            <p className="mt-0.5 text-tactical-red">{r.error}</p>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                {!batchPreflight.ok && batchPreflight.firstFailure && (
                  <div
                    data-testid="batch-first-failure"
                    data-first-failure-index={batchPreflight.firstFailure.index}
                    data-first-failure-kind={batchPreflight.firstFailure.kind}
                    className="rounded-md border border-tactical-red/40 bg-tactical-red/10 p-3 text-xs text-foreground"
                  >
                    {batchLastAction === "retry" && (
                      <div
                        data-testid="batch-retry-summary"
                        data-attempt={batchAttempt}
                        data-failed-file={batchPreflight.firstFailure.label}
                        data-failed-kind={batchPreflight.firstFailure.kind}
                        data-failed-code={batchPreflight.firstFailure.result.code ?? ""}
                        className="mb-3 rounded-md border border-sovereign-gold/40 bg-sovereign-gold/10 p-2 text-foreground"
                      >
                        <p className="font-mono text-[10px] uppercase tracking-widest text-sovereign-gold">
                          Retry attempt #{batchAttempt} · still failing
                        </p>
                        <p className="mt-1">
                          <span className="font-mono">{batchPreflight.firstFailure.label}</span>{" "}
                          ({batchPreflight.firstFailure.kind}) is still blocking the batch.
                        </p>
                        <p className="mt-1 text-foreground/80">
                          <strong>Exact fix:</strong>{" "}
                          {batchPreflight.firstFailure.result.error ?? "Replace this file and retry."}
                        </p>
                      </div>
                    )}
                    <p className="mb-2">
                      <strong className="text-tactical-red">First failure:</strong>{" "}
                      <span className="font-mono">{batchPreflight.firstFailure.label}</span>{" "}
                      ({batchPreflight.failedCount} of {batchPreflight.results.length} files failed). Fix this file first, then re-run.
                    </p>
                    {batchPreflight.firstFailure.result.guidance && (
                      <ul
                        data-testid="batch-first-failure-guidance"
                        className="ml-4 list-disc space-y-0.5 text-foreground/80"
                      >
                        {batchPreflight.firstFailure.result.guidance.map((g) => (
                          <li key={g}>{g}</li>
                        ))}
                      </ul>
                    )}
                    <button
                      type="button"
                      onClick={retryBatch}
                      data-testid="batch-preflight-try-again"
                      className="mt-3 inline-flex items-center gap-1 rounded-md border border-sovereign-gold/50 bg-sovereign-gold/10 px-3 py-1.5 text-xs font-medium text-sovereign-gold hover:bg-sovereign-gold/20"
                    >
                      <RefreshCw className="h-3 w-3" /> Try again with same files
                    </button>
                     {/* Collapsible details drawer for the failed file. */}
                     <Collapsible
                       open={failedDetailsOpen}
                       onOpenChange={(open) => {
                         setFailedDetailsOpen(open);
                         // Persist drawer state across refreshes within the
                         // current last batch attempt so users return to the
                         // same view they left.
                         if (lastBatchInputsRef.current) {
                           saveLastBatchAttempt({
                             mode,
                             template,
                             lastAction: batchLastAction,
                             attempt: batchAttempt || 1,
                             files: lastBatchInputsRef.current.map((f) => ({
                               kind: f.kind,
                               label: f.label ?? f.kind,
                               sizeBytes: f.sizeBytes,
                             })),
                             failedDetailsOpen: open,
                           });
                         }
                       }}
                       className="mt-3"
                     >
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          data-testid="batch-failed-details-trigger"
                          data-open={failedDetailsOpen ? "true" : "false"}
                          aria-expanded={failedDetailsOpen}
                          className="inline-flex items-center gap-1 rounded-md border border-border/50 bg-background/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
                        >
                          {failedDetailsOpen ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronRight className="h-3 w-3" />
                          )}
                          {failedDetailsOpen ? "Hide" : "Show"} full preflight payload
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent
                        data-testid="batch-failed-details-drawer"
                        className="mt-2 rounded-md border border-border/50 bg-background/60 p-3"
                      >
                        <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                          First validator that failed:{" "}
                          <span
                            className="text-tactical-red"
                            data-testid="batch-failed-validator"
                          >
                            {batchPreflight.firstFailure.result.code ?? "UNKNOWN"}
                          </span>{" "}
                          · file index {batchPreflight.firstFailure.index} ·
                          retryable:{" "}
                          {batchPreflight.firstFailure.result.retryable ? "yes" : "no"}
                        </p>
                        <pre
                          data-testid="batch-failed-payload"
                          className="max-h-64 overflow-auto rounded-md bg-background/80 p-2 font-mono text-[11px] leading-relaxed text-foreground/90"
                        >
{JSON.stringify(
  {
    index: batchPreflight.firstFailure.index,
    label: batchPreflight.firstFailure.label,
    kind: batchPreflight.firstFailure.kind,
    result: batchPreflight.firstFailure.result,
  },
  null,
  2,
)}
                        </pre>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                )}
                {batchPreflight.ok && (
                  <p
                    data-testid="batch-preflight-success"
                    className="rounded-md border border-tactical-green/40 bg-tactical-green/10 p-3 text-xs text-tactical-green"
                  >
                    All {batchPreflight.results.length} files passed preflight. Analyzer can start.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 7 — PDF download + share card + metrics link */}
        <div className="mb-10 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div
            data-testid="analyzer-pdf-card"
            className="rounded-2xl border border-sovereign-gold/20 bg-card/40 p-6"
          >
            <div className="mb-3 flex items-center gap-2">
              <Download className="h-4 w-4 text-sovereign-gold" />
              <p className="font-mono text-[10px] uppercase tracking-widest text-sovereign-gold">
                Download analyzer report
              </p>
            </div>
            <p className="mb-4 text-sm text-muted-foreground">
              Export the sample (or your real after-action) report as a PDF — gesture,
              vocal tone, delivery, and content highlights, ready to share with a coach
              or chief of staff.
            </p>
            <Button
              variant="sovereign"
              size="sm"
              onClick={handleDownloadSamplePdf}
              data-testid="download-sample-pdf"
              className="gap-2"
              disabled={samplePdfBusy}
              aria-busy={samplePdfBusy}
            >
              {samplePdfBusy ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {samplePdfBusy ? `${samplePdfLabel || "Generating PDF"}… ${samplePdfProgress}%` : "Download sample PDF"}
            </Button>
            {samplePdfBusy && (
              <Progress
                value={samplePdfProgress}
                data-testid="download-sample-pdf-progress"
                data-progress={samplePdfProgress}
                data-stage={samplePdfStage}
                className="mt-2 h-1"
              />
            )}
          </div>

          <div
            data-testid="share-deeplink-card"
            className="rounded-2xl border border-sovereign-gold/20 bg-card/40 p-6"
          >
            <div className="mb-3 flex items-center gap-2">
              <Link2 className="h-4 w-4 text-sovereign-gold" />
              <p className="font-mono text-[10px] uppercase tracking-widest text-sovereign-gold">
                Shareable deep-link
              </p>
            </div>
            <p className="mb-3 text-sm text-muted-foreground">
              Persists <strong className="text-foreground">mode</strong>,{" "}
              <strong className="text-foreground">template</strong>, and{" "}
              <strong className="text-foreground">retention</strong> so it opens with the
              same selections on any device.
            </p>
            <div className="mb-3 break-all rounded-md border border-border/40 bg-background/60 p-2 font-mono text-[11px] text-muted-foreground" data-testid="share-deeplink-url">
              {shareUrl}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyShare}
              data-testid="copy-share-link"
              className="gap-2"
            >
              <Copy className="h-3.5 w-3.5" />
              {shareCopied ? "Copied!" : "Copy deep-link"}
            </Button>
          </div>
        </div>

        {/* 7b — Per retention × template share matrix */}
        <div
          data-testid="share-matrix-card"
          className="mb-10 rounded-2xl border border-sovereign-gold/20 bg-card/40 p-6 backdrop-blur md:p-8"
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Link2 className="h-4 w-4 text-sovereign-gold" />
              <p className="font-mono text-[10px] uppercase tracking-widest text-sovereign-gold">
                Share matrix · per retention × template
              </p>
            </div>
            <div
              role="radiogroup"
              aria-label="Share matrix mode filter"
              data-testid="share-matrix-mode-filter"
              data-active={matrixModeFilter}
              className="inline-flex rounded-md border border-border/40 bg-background/60 p-0.5 font-mono text-[10px] uppercase tracking-widest"
            >
              <button
                type="button"
                role="radio"
                aria-checked={matrixModeFilter === "current"}
                onClick={() => setMatrixModeFilter("current")}
                data-testid="share-matrix-filter-current"
                className={`rounded px-2 py-1 transition-colors ${
                  matrixModeFilter === "current"
                    ? "bg-sovereign-gold/20 text-sovereign-gold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Current mode
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={matrixModeFilter === "all"}
                onClick={() => setMatrixModeFilter("all")}
                data-testid="share-matrix-filter-all"
                className={`rounded px-2 py-1 transition-colors ${
                  matrixModeFilter === "all"
                    ? "bg-sovereign-gold/20 text-sovereign-gold"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                All modes
              </button>
            </div>
            <button
              type="button"
              onClick={handleCopyMatrixFilter}
              data-testid="copy-matrix-filter-link"
              data-matrix-filter={matrixModeFilter}
              data-share-url={buildShareUrl(mode, template, retention, matrixModeFilter)}
              className="inline-flex items-center gap-1 rounded-md border border-sovereign-gold/40 bg-background/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-sovereign-gold hover:bg-sovereign-gold/10"
              title="Copy a link that opens the matrix in this same Current/All filter"
            >
              <Copy className="h-3 w-3" />
              {matrixFilterCopied ? "Copied!" : "Copy filter link"}
            </button>
            <button
              type="button"
              onClick={() => setShowMatrixQr((v) => !v)}
              data-testid="matrix-filter-qr-toggle"
              data-open={showMatrixQr ? "true" : "false"}
              aria-expanded={showMatrixQr}
              className="inline-flex items-center gap-1 rounded-md border border-sovereign-gold/40 bg-background/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-sovereign-gold hover:bg-sovereign-gold/10"
              title="Show a QR code so you can scan and open the link on a phone"
            >
              <QrCode className="h-3 w-3" />
              {showMatrixQr ? "Hide QR" : "Show QR"}
            </button>
          </div>
          {showMatrixQr && (
            <div
              data-testid="matrix-filter-qr-card"
              data-share-url={buildShareUrl(mode, template, retention, matrixModeFilter)}
              className="mb-4 flex items-center gap-4 rounded-md border border-sovereign-gold/30 bg-background/60 p-4"
            >
              <div className="rounded-md bg-white p-2">
                <QRCodeSVG
                  data-testid="matrix-filter-qr-svg"
                  value={buildShareUrl(mode, template, retention, matrixModeFilter)}
                  size={128}
                  includeMargin={false}
                />
              </div>
              <div className="flex-1 text-xs text-muted-foreground">
                <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-sovereign-gold">
                  Scan to share on mobile
                </p>
                <p className="break-all font-mono text-[10px] text-foreground/80">
                  {buildShareUrl(mode, template, retention, matrixModeFilter)}
                </p>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Opens the matrix card in the same{" "}
                  <strong className="text-foreground">
                    {matrixModeFilter === "current" ? "Current mode" : "All modes"}
                  </strong>{" "}
                  view.
                </p>
              </div>
            </div>
          )}
          <p className="mb-4 text-sm text-muted-foreground">
            One-click share for every retention × template combination. Each link
            preserves <strong className="text-foreground">mode</strong>,{" "}
            <strong className="text-foreground">template</strong>, and{" "}
            <strong className="text-foreground">retention</strong> in the URL hash —
            recipients land on exactly the rehearsal you set.
          </p>
          {(matrixModeFilter === "current" ? [mode] : (["home", "office"] as const)).map((m) => {
            const modeLabel = MODES.find((x) => x.id === m)!.label;
            return (
              <div key={m} className="mb-4 last:mb-0" data-testid={`share-matrix-section-${m}`}>
                <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Mode: <span className="text-foreground">{modeLabel}</span>
                </p>
                <div className="overflow-x-auto rounded-md border border-border/40">
                  <table className="w-full text-xs">
                    <thead className="bg-secondary/40 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left">Template ↓ / Retention →</th>
                        {RETENTION_OPTIONS.map((r) => (
                          <th key={r.id} className="px-3 py-2 text-left">{r.label.split(" ")[0]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {TEMPLATES.map((t) => (
                        <tr key={t.id} className="border-t border-border/40">
                          <td className="px-3 py-2 font-semibold text-foreground">{t.title}</td>
                          {RETENTION_OPTIONS.map((r) => {
                            const key = `${m}-${t.id}-${r.id}`;
                            const copied = matrixCopied === key;
                            return (
                              <td key={r.id} className="px-3 py-2">
                                <button
                                  type="button"
                                  onClick={() => handleCopyMatrix(m, t.id, r.id)}
                                  data-testid={`copy-matrix-${m}-${t.id}-${r.id}`}
                                  data-share-url={buildShareUrl(m, t.id, r.id)}
                                  className="inline-flex items-center gap-1 rounded-md border border-sovereign-gold/40 bg-background/40 px-2 py-1 font-mono text-[11px] text-sovereign-gold hover:bg-sovereign-gold/10"
                                >
                                  <Copy className="h-3 w-3" />
                                  {copied ? "Copied!" : "Copy link"}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
          <p className="mt-3 font-mono text-[10px] text-muted-foreground">
            {matrixModeFilter === "current"
              ? <>Showing <strong className="text-foreground">{selectedMode.label}</strong> only — toggle "All modes" to share both.</>
              : <>Showing both home + office modes (6 templates × 3 retentions = 18 links).</>}
          </p>
        </div>

        {/* Final deep-link CTA */}
        <div className="text-center">
          <Link
            to={launchHref}
            data-testid="self-debate-launch-cta"
            aria-disabled={!consentReady}
            onClick={(e) => {
              if (!consentReady) {
                e.preventDefault();
                toast({
                  title: "Consent required",
                  description: "Confirm both consent boxes before launching the studio.",
                  variant: "destructive",
                });
              }
            }}
          >
            <Button
              variant="sovereign"
              size="lg"
              className="gap-2"
              disabled={!consentReady}
              data-testid="self-debate-launch-button"
            >
              Open Oratory Studio with these settings
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
          <p className="mt-2 text-xs text-muted-foreground">
            Your selections are passed to the studio so you can start rehearsing in one click.
          </p>
          <Link
            to="/metrics/self-debate-privacy"
            data-testid="metrics-dashboard-link"
            className="mt-4 inline-flex items-center gap-1.5 text-xs text-sovereign-gold underline-offset-4 hover:underline"
          >
            <BarChart3 className="h-3.5 w-3.5" />
            View public privacy-settings adoption dashboard
          </Link>
        </div>
      </div>
      {manualCopyText && (
        <div
          data-testid="manual-copy-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="manual-copy-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setManualCopyText(null)}
        >
          <div
            className="w-full max-w-md rounded-lg border border-sovereign-gold/30 bg-card p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-start justify-between gap-3">
              <div>
                <p
                  id="manual-copy-title"
                  className="font-mono text-[10px] uppercase tracking-widest text-sovereign-gold"
                >
                  Copy manually
                </p>
                <h3 className="mt-1 font-serif text-lg font-bold text-foreground">
                  Clipboard unavailable
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setManualCopyText(null)}
                data-testid="manual-copy-close"
                aria-label="Close"
                className="rounded-md p-1 text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">
              Your browser blocked clipboard access. Select the link below and copy it manually
              (long-press on mobile, or Cmd/Ctrl+C on desktop).
            </p>
            <textarea
              data-testid="manual-copy-textarea"
              readOnly
              value={manualCopyText}
              onFocus={(e) => e.currentTarget.select()}
              className="h-24 w-full rounded-md border border-border/50 bg-background/80 p-2 font-mono text-[11px] leading-relaxed text-foreground/90"
            />
            <div className="mt-3 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setManualCopyText(null)}
                data-testid="manual-copy-done"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SelfDebateStudioPreview;